import { useRef } from 'react'
import { useRequest } from 'ahooks'

import { Table } from '@sar/component'
import { DatePicker, Select } from 'antd'

import { UploadImage, Remark } from 'components'
import { ModalForm, ProFormSwitch, ProFormDatePicker, ProFormSelect, ProFormDependency } from '@ant-design/pro-components'

async function fetchData (params, store) {
  const result = await store.externals.api.get(`/assemble?type=send`, {
    params: Object.assign(params, {
      page: params.current,
      size: params.pageSize,
    })
  })

  return {
    success: true,
    data: result.data,
    total: result.totals,
  }
}

function Update ({ data, store, visible, handles, onVisibleChange }) {
  return <ModalForm
    title='修改配送'
    layout='vertical'
    width={500}
    open={visible}
    modalProps={{
      centered: true,
      maskClosable: false,
      destroyOnClose: true,
    }}
    onOpenChange={onVisibleChange}
    request={() =>  store.externals.api.get(`/assemble/info/${data.id}?type=send`)}
    onFinish={params => store.externals.api.put(`/assemble/update/${data.id}?type=send`, { ...params, sendTime: moment(params.sendTime).format('YYYY-MM-DD HH:mm:ss') }).then(() => handles.current.reload()).then(R.T)}
  >
    <ProFormDatePicker
      width='xl'
      name='sendTime'
      label='送货时间'
      rules={[ { required: true, message: '请选择送货时间' } ]}
      fieldProps={{ showTime: { format: 'HH:mm' }, format: 'YYYY-MM-DD HH:mm' }}
    />

    <ProFormSelect
      name='sendStatus'
      label='配送状态'
      rules={[ { required: true, message: '请选择配送状态' } ]}
      options={[
        {
          label: '未配送',
          value: 0,
        },
        {
          label: '已配送',
          value: 1,
        },
      ]}
    />

    <ProFormSelect
      name='deliveryMethod'
      label='配送方式'
      rules={[ { required: true, message: '请选择配送方式' } ]}
      valueEnum={store.config.getConstantEnums('DeliveryMethod')}
    />

    <ProFormDependency name={[ 'deliveryMethod' ]}>
      {({ deliveryMethod }) => {
        if (deliveryMethod != '配送') {
          return null
        }

        return <ProFormSelect
          showSearch
          name='sendPeopleId'
          label='配送人员'
          rules={[ { required: true, message: '请选择配送人员' } ]}
          request={() => store.externals.api.get(`/emp/all`)}
          fieldProps={{ optionFilterProp: 'nickname', fieldNames: { label: 'nickname', value: 'id' } }}
        />
      }}
    </ProFormDependency>

    <ProFormSwitch
      name='installType'
      label='是否需要安装'
      rules={[ { required: true, message: '请选择是否需要安装' } ]}
      convertValue={value => R.is(String, value) ? value == 'yes' : value}
      transform={value => ({ installType: R.is(String, value) ? value : value ? 'yes' : 'no' })}
    />
  </ModalForm>
}

export default function ({ store, handles = useRef<any>(), searchParams }) {
  const [ state, dispatch ] = React.useState<any>({
    selected: R.defaultTo('0', searchParams.get('selected'))
  })

  const columns = [
    {
      title: '序号',
      dataIndex: 'id',
    },
    {
      title: '订单编号',
      dataIndex: 'orderNumber',
    },
    {
      title: '订单状态',
      dataIndex: 'orderStatus',
      valueType: 'select',
      valueEnum: store.config.getConstantEnums(`OrderStatus`),
    },
    {
      title: '收货人',
      dataIndex: 'receiver',
      formItemProps: () => ({ label: false }),
      fieldProps: () => ({ placeholder: '收货人' }),
    },
    {
      title: '联系电话',
      dataIndex: 'phoneNumber',
      render: _ => _.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2'),
      formItemProps: () => ({ label: false }),
      fieldProps: () => ({ placeholder: '联系电话' }),
    },
    {
      title: '收货地址',
      dataIndex: 'deliveryAddress',
      formItemProps: () => ({ label: false }),
      fieldProps: () => ({ placeholder: '收货地址' }),
    },
    {
      title: '来源',
      dataIndex: 'customerSourceId',
      valueType: 'treeSelect',
      hideInTable: true,
      request: () => store.externals.api.get(`/constant/source`),
      formItemProps: () => ({ label: false }),
      fieldProps: () => ({ fieldNames: { value: 'id', label: 'source' }, placeholder: '来源' }),
    },
    {
      title: '来源',
      dataIndex: 'customerSource',
    },
    {
      title: '送货时间',
      dataIndex: 'sendTime',
    },
    {
      title: '配送状态',
      dataIndex: 'sendStatus',
      valueType: 'select',
      valueEnum: { 0: '未配送', 1: '已配送' },
    },
    {
      title: '配送方式',
      dataIndex: 'deliveryMethod',
      valueType: 'select',
      valueEnum: store.config.getConstantEnums('DeliveryMethod'),
    },
    {
      title: '配送人员',
      dataIndex: 'sendPeople',
    },
    {
      title: '现场图片',
      dataIndex: 'upSendImg',
      render: (_, data) => <a className='text-blue-600' onClick={() => dispatch({ ...state, open: true, id: data.orderId, readonly: data.orderStatus == 'end' || !store.identity.hasPermission('/assembly/delivery:upload') || data.sendStatus == 1 })}>{data.upSendImg ? '查看' : '上传'}</a>,
    }
  ]

  const cols = React.useMemo(() => {
    return columns.map(column => {
      let keys = [
          'receiver'
        , 'phoneNumber'
        , 'deliveryAddress'
        , 'customerSourceId'
      ]

      return keys.includes(column.dataIndex) ? column : Object.assign(column, { hideInSearch: true })
    })
  }, [
    columns,
  ])

  const rowActions = [
    {
      name: '修改',
      form: props => <Update {...props} store={store} />,
      visible: (data) => store.identity.hasPermission('/assembly/delivery:update') && data.sendStatus == 0,
    },
    {
      name: '确定',
      confirmType: 'modal',
      confirmMessage: '确定完成配送吗，将不可更改。',
      visible: (data) => store.identity.hasPermission('/assembly/delivery:submit') && data.sendStatus == 0,
      onConfirm: (data, handles) => store.externals.api.put(`/assemble/send/${data.id}`).then(() => handles.current.reload()),
    },
    {
      name: '查看备注',
      form: props => <Remark {...props}/>,
    },
  ]

  const handleUploadSave = useRequest(
    async data => {
      return store.externals.api.put(`/assemble/file/${state.id}?type=sendImg`, data.map(elm => {
        return {
          url: elm.url,
          fileName: elm.filename,
        }
      }))
    },
    {
      manual: true,
    },
  )

  return <div className='p-4'>
    <Table
      handles={handles}
      title='配送列表'
      columns={cols}
      rowActions={rowActions}
      request={params => fetchData(params, store)}
      params={{ status: state.selected }}
      scroll={{ }}
      search={{ labelWidth: 'auto', defaultCollapsed: true, span: Object.assign({}, store.config.responsive, { xxl: 4 }) }}
      form={{
        initialValues: {
          receiver: searchParams.get('receiver'),
          phoneNumber: searchParams.get('phoneNumber'),
          deliveryAddress: searchParams.get('deliveryAddress'),
        }
      }}
      toolbar={{
        multipleLine: true,
        tabs: {
          activeKey: state.selected,
          onChange: data => dispatch({ ...state, selected: data }),
          items: [
            { key: '0', tab: '未完成' },
            { key: '1', tab: '已完成' },
          ],
        },
      }}
    />

    <ModalForm
      open={state.open}
      width={600}
      modalProps={{
        centered: true,
        destroyOnClose: true,
      }}
      rowProps={{ gutter: 24 }}
      layout='vertical'
      request={async () => {
        const result = await store.externals.api.get(`/assemble/${state.id}/file`, {
          params: {
            type: 'sendImg',
          }
        })

        return {
          files: result.map(e => ({ url: e.url, filename: e.fileName }))
        }
      }}
      submitter={{
        searchConfig: {
          submitText: '关闭',
        },
        resetButtonProps: {
          style: {
            display: 'none',
          },
        },
      }}
      onFinish={async () => true}
      onOpenChange={e => dispatch({ ...state, open: e })}
    >
      <UploadImage
        max={100000}
        name='files'
        label='上传图片文件'
        readonly={state.readonly}
        onSave={e => handleUploadSave.runAsync(e).then(() => handles.current.reload())}
        fieldProps={{ upload: file => store.oss.client.multipartUpload(`${store.externals.nonce()}.${file.name.split('.').pop()}`, file).then(res => `//${store.oss.endpoint}/${res.name}`) }}
      />
    </ModalForm>
  </div>
}
